import { useEffect, useState } from "react";
import type { ActiveWork, Session } from "../types.js";

function elapsed(since: string, now: number, priorSeconds = 0): string {
  const live = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  // Time before this session is added in, so a pause for lunch does not
  // send the clock back to zero and make the morning look lost.
  const seconds = live + priorSeconds;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Always on screen while a timer runs.
 *
 * The most common way a tool like this produces nonsense is a timer
 * somebody forgot was running. The nightly sweep is a backstop for that,
 * not a fix — the fix is that you cannot look at this screen without
 * seeing it, and the clock ticks so it reads as live rather than as a
 * label someone left behind.
 *
 * Two sizes. On the work screen it is the running task itself — big
 * enough to read from the cart — so the screen does not repeat the name
 * in a card underneath. Everywhere else it is one slim line, because a
 * lead in the task screen needs the evidence, not the controls.
 */
export function TrackingBanner({
  session,
  work,
  size = "slim",
}: {
  session: Session;
  work: ActiveWork | null;
  size?: "hero" | "slim";
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const clock = (
    <time className="elapsed" dateTime={session.startedAt}>
      {elapsed(session.startedAt, now, work?.priorSeconds ?? 0)}
    </time>
  );

  if (size === "hero") {
    return (
      <div className="banner hero" role="status">
        <div className="banner-top">
          <span className="banner-dot" aria-hidden="true" />
          <span className="banner-label">Running</span>
          {work?.label ? <code>{work.label}</code> : null}
        </div>
        <strong className="banner-task">{work?.name ?? "Task"}</strong>
        <div className="banner-bottom">
          <span className="banner-meta">{work?.location ?? ""}</span>
          {clock}
        </div>
      </div>
    );
  }

  return (
    <div className="banner" role="status">
      <span className="banner-dot" aria-hidden="true" />
      <span className="banner-label">Running</span>
      <strong className="banner-task">
        {work?.name ?? "Task"}
        {work?.location ? <span> · {work.location}</span> : null}
      </strong>
      {work?.label ? <code>{work.label}</code> : null}
      {clock}
    </div>
  );
}
